<template>
  <v-container
    id="site-interface-editor"
    :class="{ disabled: processing }"
    fluid
  >
    <v-row>
      <v-col cols="12">
        <h3>
          {{
            user
              ? 'Edition des rattachements de sites'
              : "Edition des rattachements d'utilisateurs"
          }}
        </h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          class="mr-2"
          v-model="available.search"
          prepend-icon="search"
          label="Rechercher..."
          single-line
          hide-details
        />
      </v-col>
      <v-col>
        <v-text-field
          class="ml-2"
          v-model="linked.search"
          prepend-icon="search"
          label="Rechercher..."
          single-line
          hide-details
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="dual-table-panel">
          <div class="panel-available">
            <v-skeleton-loader
              v-if="loading === true"
              type="table"
              tile
              class="mx-auto"
            />
            <v-data-table
              v-else
              class="mr-2 elevation-2"
              v-model="available.selected"
              show-select
              :search="available.search"
              :headers="headers"
              :items="available.items"
              :item-key="dataTableKey"
            >
              <template v-slot:top>
                <h4 class="py-2 pl-4">
                  {{
                    `${
                      user
                        ? 'Sites non rattachés à cet utilisateur'
                        : 'Utilisateurs non rattachés à ce site'
                    } (${available.items.length})`
                  }}
                </h4>
              </template>
              <template v-slot:item.action="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="scdf-yellow"
                      fab
                      small
                      v-on="on"
                      @click="linkItem(item)"
                    >
                      <v-icon>add</v-icon>
                    </v-btn>
                  </template>
                  {{
                    user
                      ? 'Rattacher ce site à cet utilisateur'
                      : 'Rattacher cet utilisateur à ce site'
                  }}
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
          <div class="panel-linked">
            <v-skeleton-loader
              v-if="loading === true"
              type="table"
              tile
              class="mx-auto"
            />
            <v-data-table
              v-else
              class="ml-2 elevation-2"
              show-select
              v-model="linked.selected"
              :search="linked.search"
              :headers="headers"
              :items="linked.items"
              :item-key="dataTableKey"
            >
              <template v-slot:top>
                <h4 class="py-2 pl-4">
                  {{
                    `${
                      user
                        ? 'Sites rattachés à cet utilisateur'
                        : 'Utilisateurs rattachés à ce site'
                    } (${linked.items.length})`
                  }}
                </h4>
              </template>
              <template v-slot:item.action="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      class="scdf-red"
                      fab
                      small
                      v-on="on"
                      @click="unlinkItem(item)"
                    >
                      <v-icon>remove</v-icon>
                    </v-btn>
                  </template>
                  {{
                    `${
                      user
                        ? "Détacher ce site de l'utilisateur"
                        : 'Détacher cet utilisateur de ce site'
                    }`
                  }}
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <template v-if="available.selected.length > 0">
          <v-btn class="mr-2 scdf-yellow" @click="linkItems()">
            Rattacher {{ available.selected.length }}
            {{ user ? 'site' : 'utilisateur'
            }}{{ available.selected.length > 1 ? 's' : '' }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn disabled class="mr-2 scdf-yellow" @click="linkItems()">
            Rattacher
          </v-btn>
        </template>
      </v-col>
      <v-col>
        <template v-if="linked.selected.length > 0">
          <v-btn class="ml-2 scdf-red" @click="unlinkItems()">
            Détacher {{ linked.selected.length }}
            {{ user ? 'site' : 'utilisateur'
            }}{{ linked.selected.length > 1 ? 's' : '' }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn disabled class="ml-2 scdf-red" @click="unlinkItems()">
            Détacher
          </v-btn>
        </template>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn @click="cancelEdit()">
          <v-icon class="mr-1">keyboard_arrow_left</v-icon>
          RETOUR
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SiteInterfaceEditor',
  props: {
    dataTableKey: {
      type: String,
      default: null,
    },
    headers: {
      type: Array,
      default: null,
    },
    user: {
      type: Object,
      default: null,
    },
    site: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      linked: {
        items: [],
        itemsIds: {}, // update a json indexer with IDs only for faster array operations.
        search: '',
        selected: [],
      },
      available: {
        items: [],
        search: '',
        selected: [],
      },
      adminUser: this.$store.state.user,
      coop: null,
      loading: true,
      processing: false,
    };
  },
  created() {
    this.loading = true;
    this.load().finally(() => {
      this.loading = false;
    });
  },
  methods: {
    mapUriListBody(arr) {
      const newArr = arr.map(e => (this.user ? 'sites/' : 'users/') + e);
      const result = newArr.map(JSON.stringify).join('\n') + '\n';
      return result.replace(new RegExp('"', 'g'), '');
    },
    getItemsIds(linkedItems) {
      this.linked.itemsIds = {};
      if (this.user) {
        linkedItems.forEach(e => {
          this.linked.itemsIds[e.refSite.id] = true;
        });
      } else if (this.site) {
        linkedItems.forEach(e => {
          this.linked.itemsIds[e.id] = true;
        });
      }
    },
    resetItems() {
      this.linked.items = [];
      this.available.items = [];
    },
    resetSelected() {
      this.linked.selected = [];
      this.available.selected = [];
    },
    load() {
      return new Promise((resolve, reject) => {
        this.resetItems();
        let allItems = null;
        let linkedItems = null;

        const linkedItemsUrl = this.user
          ? process.env.VUE_APP_BASE_URL +
            process.env.VUE_APP_USER_REPOSITORY +
            `/${this.user.id}?projection=site`
          : process.env.VUE_APP_BASE_URL +
            process.env.VUE_APP_SITE_REPOSITORY +
            `/${this.site.refSite.id}/users`;

        const allItemsUrl = this.user
          ? process.env.VUE_APP_BASE_URL +
            process.env.VUE_APP_SITES_COOPERATIVES_REPOSITORY +
            `/search/findByRefCooperativeIdEquals?projection=site&refCooperativeId=${this.$store.state.userCoopId}`
          : process.env.VUE_APP_BASE_URL + process.env.VUE_APP_USER_CHILDREN;

        const allItemsPromise = axios({
          method: 'get',
          url: allItemsUrl,
          headers: {
            user: this.adminUser,
          },
        }).then(res => {
          if (this.user) {
            allItems = res.data._embedded.datas;
          } else if (this.site) {
            allItems = res.data;
          }
        });

        const linkedItemsPromise = axios({
          method: 'get',
          url: linkedItemsUrl,
          headers: {
            user: this.adminUser,
          },
        }).then(res => {
          if (this.user) {
            linkedItems = res.data.sites;
          } else if (this.site) {
            linkedItems = res.data._embedded.datas;
          }
          this.getItemsIds(linkedItems);
        });

        Promise.all([allItemsPromise, linkedItemsPromise])
          .then(() => {
            if (this.user) {
              linkedItems.forEach(e => {
                this.linked.items.push(e);
              });
              allItems.forEach(e => {
                if (!this.linked.itemsIds[e.site.refSite.id]) {
                  this.available.items.push(e.site);
                }
              });
            } else if (this.site) {
              linkedItems.forEach(e => {
                this.linked.items.push({
                  id: e.id,
                  email: e.email,
                  firstName: e.firstName,
                  lastName: e.lastName,
                });
              });
              allItems.forEach(e => {
                if (!this.linked.itemsIds[e.id]) {
                  this.available.items.push({
                    id: e.id,
                    email: e.email,
                    firstName: e.firstName,
                    lastName: e.lastName,
                  });
                }
              });
            }
            resolve();
          })
          .catch(e => {
            reject(e);
          });
      });
    },
    cancelEdit() {
      this.$emit('cancel-edit');
    },
    linkItem(item) {
      if (this.user) {
        this.linked.itemsIds[item.refSite.id] = true;
      } else if (this.site) {
        this.linked.itemsIds[item.id] = true;
      }
      this.putItems();
    },
    unlinkItem(item) {
      if (this.user) {
        delete this.linked.itemsIds[item.refSite.id];
      } else if (this.site) {
        delete this.linked.itemsIds[item.id];
      }
      this.putItems();
    },
    linkItems() {
      if (this.user) {
        this.available.selected.forEach(e => {
          this.linked.itemsIds[e.refSite.id] = true;
        });
      } else if (this.site) {
        this.available.selected.forEach(e => {
          this.linked.itemsIds[e.id] = true;
        });
      }
      this.putItems();
    },
    unlinkItems() {
      if (this.user) {
        this.linked.selected.forEach(e => {
          delete this.linked.itemsIds[e.refSite.id];
        });
      } else if (this.site) {
        this.linked.selected.forEach(e => {
          delete this.linked.itemsIds[e.id];
        });
      }
      this.putItems();
    },
    putItems() {
      this.processing = true;
      const uriListBody = this.mapUriListBody(
        Object.keys(this.linked.itemsIds)
      );

      const putUrl = this.user
        ? process.env.VUE_APP_BASE_URL +
          process.env.VUE_APP_USER_REPOSITORY +
          `/${this.user.id}/sites`
        : process.env.VUE_APP_BASE_URL +
          process.env.VUE_APP_SITE_REPOSITORY +
          `/${this.site.refSite.id}/users`;

      axios({
        url: putUrl,
        method: 'put',
        headers: {
          user: this.adminUser,
          'Content-Type': 'text/uri-list',
        },
        data: uriListBody,
      })
        .then(() => {
          return true;
        })
        .catch(() => {
          return false;
        })
        .finally(() => {
          this.resetSelected();
          this.load().finally(() => {
            this.$emit('update');
            this.processing = false;
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
#site-interface-editor {
  &.disabled {
    opacity: 0.25;
    pointer-events: none;
  }
}
.dual-table-panel {
  width: 100%;

  .panel-available {
    display: inline-block;
    white-space: normal;
    -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
    -moz-box-sizing: border-box; /* Firefox, other Gecko */
    box-sizing: border-box; /* Opera/IE 8+ */
    width: 50%;
  }
  .panel-linked {
    display: inline-block;
    white-space: normal;
    -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
    -moz-box-sizing: border-box; /* Firefox, other Gecko */
    box-sizing: border-box; /* Opera/IE 8+ */
    width: 50%;
  }
}
</style>
